import React, { useState, useEffect } from 'react';
import api from '../../api/axios';

const EMOJI_MAP = { angry: '😠', sad: '😢', neutral: '😐', happy: '😊', excited: '🤩' };
const COLORS    = { angry: '#ef4444', sad: '#f97316', neutral: '#94a3b8', happy: '#22c55e', excited: '#8b5cf6' };
const SENTIMENT_SCORE = { angry: 1, sad: 2, neutral: 3, happy: 4, excited: 5 };

const FILTERS = [
    { id: 'today',     label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'thisWeek',  label: 'This Week' },
    { id: 'lastWeek',  label: 'Last Week' },
    { id: 'custom',    label: 'Custom' },
    { id: '',          label: 'All Time' },
];

function SentimentBar({ counts }) {
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    if (!total) return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No data</span>;
    return (
        <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', gap: 1, width: '100%' }}>
            {Object.entries(counts).map(([k, v]) =>
                v > 0 ? <div key={k} title={`${k}: ${v}`} style={{ flex: v, background: COLORS[k], minWidth: 2 }} /> : null
            )}
        </div>
    );
}

function MiniTrend({ trend }) {
    if (!trend || trend.length === 0) return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>;
    const max = Math.max(...trend.map(t => t.count), 1);
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 22, marginBottom: 4 }}>
            {trend.map((t, i) => (
                <div
                    key={i}
                    title={`${t.date}: ${t.count}`}
                    style={{
                        flex: 1,
                        height: `${Math.max(10, (t.count / max) * 100)}%`,
                        background: 'var(--primary)',
                        borderRadius: '2px 2px 0 0',
                        opacity: 0.7,
                        minWidth: 4,
                    }}
                />
            ))}
        </div>
    );
}

// ── Detail panel for one location ────────────────────────────────────────────
function LocationDetail({ locId, locName, filter, startDate, endDate, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let url = `/location-performance/${locId}/detail?filter=${filter}`;
        if (filter === 'custom' && startDate && endDate) url += `&startDate=${startDate}&endDate=${endDate}`;
        api.get(url)
            .then(r => { setData(r.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, [locId, filter, startDate, endDate]);

    return (
        <div className="card" style={{ marginTop: 12, borderLeft: '3px solid var(--primary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h4>📍 {locName} - Detail</h4>
                <button className="btn btn-outline btn-sm" onClick={onClose}>✕ Close</button>
            </div>

            {loading ? <div className="spinner" /> : !data ? (
                <p className="text-muted">Failed to load.</p>
            ) : (
                <div>
                    {/* Staff breakdown */}
                    {data.staffBreakdown?.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <p style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 8 }}>
                                Staff at this location
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {data.staffBreakdown.map((s, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg)', borderRadius: 8, padding: '8px 12px' }}>
                                        <div style={{
                                            width: 28, height: 28, borderRadius: '50%',
                                            background: i < 3 ? ['#fbbf24','#94a3b8','#d97706'][i] : 'var(--border)',
                                            color: i < 3 ? '#fff' : 'var(--text-muted)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 11, fontWeight: 700, flexShrink: 0,
                                        }}>{i + 1}</div>
                                        <span style={{ flex: 1, fontWeight: 600, fontSize: 13 }}>{s.name}</span>
                                        <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 700 }}>{s.count} responses</span>
                                        <div style={{ width: 80 }}><SentimentBar counts={s.sentimentCounts} /></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Response list */}
                    <p style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 8 }}>
                        Responses ({data.responses?.length || 0})
                    </p>
                    {data.responses?.length === 0 ? (
                        <p className="text-muted" style={{ fontSize: 13 }}>No responses for this period.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
                            {data.responses.map(r => (
                                <div key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg)', borderRadius: 8, padding: '8px 12px' }}>
                                    <span style={{ fontSize: 16 }}>{r.feedbackType === 'audio' ? '🎙️' : '📋'}</span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600 }}>
                                            {r.feedbackTaker?.name || 'Unknown'}
                                            {r.billId && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>· Bill: {r.billId}</span>}
                                        </div>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(r.createdAt).toLocaleString()}</div>
                                    </div>
                                    {r.overallSentiment && (
                                        <span style={{ fontSize: 18 }}>{EMOJI_MAP[r.overallSentiment]}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LocationPerformanceTab() {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('thisWeek');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const [error, setError] = useState('');

    const load = async () => {
        setLoading(true); setError('');
        try {
            let url = `/location-performance?filter=${filter}`;
            if (filter === 'custom' && startDate && endDate) url += `&startDate=${startDate}&endDate=${endDate}`;
            const { data } = await api.get(url);
            setLocations(data.data);
        } catch { setError('Failed to load location performance'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [filter, startDate, endDate]);

    const totalResponses = locations.reduce((s, l) => s + l.totalResponses, 0);

    // Find best and worst locations by avg score
    const scored = locations.filter(l => l.avgScore);
    const best  = scored.length ? scored.reduce((a, b) => parseFloat(a.avgScore) >= parseFloat(b.avgScore) ? a : b) : null;
    const worst = scored.length ? scored.reduce((a, b) => parseFloat(a.avgScore) <= parseFloat(b.avgScore) ? a : b) : null;

    return (
        <div>
            <div className="page-header">
                <h3>Location Performance</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Compare feedback sentiment and scores across all your branches.</p>
            </div>

            {/* Date filter */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                {FILTERS.map(f => (
                    <button
                        key={f.id}
                        className={`btn btn-sm ${filter === f.id ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setFilter(f.id)}
                    >{f.label}</button>
                ))}
            </div>

            {filter === 'custom' && (
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                        <label>Start Date</label>
                        <input type="date" className="form-control" value={startDate} onChange={e => setStartDate(e.target.value)} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                        <label>End Date</label>
                        <input type="date" className="form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
                    </div>
                </div>
            )}

            {error && <div className="alert alert-error">{error}</div>}

            {/* Summary banner */}
            {!loading && locations.length > 0 && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                    <div className="card" style={{ flex: '1 1 140px', textAlign: 'center', padding: '14px 12px' }}>
                        <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--primary)' }}>{totalResponses}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total responses</div>
                    </div>
                    <div className="card" style={{ flex: '1 1 140px', textAlign: 'center', padding: '14px 12px' }}>
                        <div style={{ fontSize: 26, fontWeight: 700 }}>{locations.filter(l => l._id).length}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Active locations</div>
                    </div>
                    {best && (
                        <div className="card" style={{ flex: '1 1 180px', padding: '14px 16px', borderLeft: '3px solid #22c55e' }}>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#15803d', marginBottom: 4 }}>⭐ Top performer</div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{best.name}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Avg score: {best.avgScore}/5</div>
                        </div>
                    )}
                    {worst && worst._id !== best?._id && (
                        <div className="card" style={{ flex: '1 1 180px', padding: '14px 16px', borderLeft: '3px solid #f97316' }}>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#c2410c', marginBottom: 4 }}>⚠️ Needs attention</div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{worst.name}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Avg score: {worst.avgScore}/5</div>
                        </div>
                    )}
                </div>
            )}

            {loading ? <div className="spinner" /> : locations.length === 0 ? (
                <div className="card text-center" style={{ padding: 56 }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>📍</div>
                    <p className="text-muted">No location data yet. Add locations and assign staff to get started.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {locations.map((loc, i) => (
                        <div key={loc._id || 'unassigned'}>
                            <div
                                className="card"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setExpandedId(expandedId === (loc._id || 'unassigned') ? null : (loc._id || 'unassigned'))}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>

                                    {/* Rank */}
                                    <div style={{
                                        width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                        background: i === 0 ? '#fbbf24' : i === 1 ? '#94a3b8' : i === 2 ? '#d97706' : 'var(--bg)',
                                        color: i < 3 ? '#fff' : 'var(--text-muted)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 700, fontSize: 14,
                                    }}>{i + 1}</div>

                                    {/* Name */}
                                    <div style={{ flex: 1, minWidth: 120 }}>
                                        <div style={{ fontWeight: 600, fontSize: 15 }}>
                                            📍 {loc.name}
                                            {loc.city && <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 13 }}>, {loc.city}</span>}
                                        </div>
                                        {!loc.isActive && <span className="badge badge-gray" style={{ fontSize: 10 }}>Inactive</span>}
                                    </div>

                                    {/* Stats */}
                                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                                            <div style={{ fontWeight: 700, fontSize: 20, color: 'var(--primary)' }}>{loc.totalResponses}</div>
                                            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Responses</div>
                                        </div>
                                        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                                                <span title="Manual" style={{ marginRight: 6 }}>📋 {loc.manualCount}</span>
                                                <span title="Audio">🎙️ {loc.audioCount}</span>
                                            </div>
                                            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>By type</div>
                                        </div>
                                        {loc.avgScore && (
                                            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                <div style={{ fontWeight: 700, fontSize: 18, color: parseFloat(loc.avgScore) >= 4 ? '#22c55e' : parseFloat(loc.avgScore) >= 3 ? '#f59e0b' : '#ef4444' }}>
                                                    {loc.avgScore}
                                                </div>
                                                <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Avg / 5</div>
                                            </div>
                                        )}
                                        {loc.dominantSentiment && (
                                            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                <div style={{ fontSize: 24 }}>{EMOJI_MAP[loc.dominantSentiment]}</div>
                                                <div style={{ fontSize: 10, color: COLORS[loc.dominantSentiment], textTransform: 'capitalize', fontWeight: 600 }}>{loc.dominantSentiment}</div>
                                            </div>
                                        )}
                                        <div style={{width: 60, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center'}}>
                                            <MiniTrend trend={loc.trend}/>
                                            <div style={{
                                                fontSize: 10,
                                                color: 'var(--text-muted)',
                                                marginBottom: 3
                                            }}>7-day trend
                                            </div>
                                        </div>
                                    </div>

                                    <span style={{fontSize: 12, color: 'var(--text-muted)', textAlign: 'right'}}>
                                        {expandedId === (loc._id || 'unassigned') ? '▲' : '▼'}
                                      </span>
                                </div>

                                {/* Sentiment bar */}
                                {loc.totalResponses > 0 && (
                                    <div style={{marginTop: 12}}>
                                        <SentimentBar counts={loc.sentimentCounts}/>
                                        <div style={{display: 'flex', gap: 8, marginTop: 10}}>
                                            {Object.entries(loc.sentimentCounts).map(([k, v]) => v > 0 && (
                                                <span key={k} style={{
                                                    fontSize: 11,
                                                    padding: '2px 8px',
                                                    borderRadius: 10,
                                                    background: COLORS[k] + '20',
                                                    color: COLORS[k],
                                                    fontWeight: 600,
                                                    textAlign: 'center'
                                                }}>
                                  {EMOJI_MAP[k]} {v}
                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {expandedId === (loc._id || 'unassigned') && (
                                <LocationDetail
                                    locId={loc._id || 'unassigned'}
                                    locName={loc.name}
                                    filter={filter}
                                    startDate={startDate}
                                    endDate={endDate}
                                    onClose={() => setExpandedId(null)}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}